import { Box, chakra, Flex, FlexProps, Text } from '@gazin/ecommerce-ui'
import { NextRouter } from 'next/router'
import { Dispatch, SetStateAction, useContext, useRef, useState } from 'react'
import ProductRewardsPoints from 'src/components/productRewardsPoints'
import PromotionFlag from 'src/components/promotionFlag'
import { ContextoGlobal } from 'src/contexto/contextoGlobal'
import {
  Ad,
  FreightResponse,
  Variation,
} from 'src/services/marketplace-api/dtos'
import { theme } from 'src/styles/themes/default'
import { ProductCartData } from 'src/types/Carrinho'
import { makeArrayQuantity } from 'src/util/products'
import { displayDiscountTagAndEvent } from 'src/util/promotionalEvent'
import { AttributeProps } from '../types'
import { BuyNowAndAddCartButtons } from './buyNowAndAddCartButtons'
import { ProductQuantity } from './productQuantity'
import { ProductValues } from './productValues'
import { SearchShipping } from './searchShipping'

type ContentValuesColumnProps = {
  adActive: Ad
  ads: Ad[]
  dataProductCart?: ProductCartData
  freight?: FreightResponse[]
  hasWarranty: boolean
  route: NextRouter
  setDataProductCart: Dispatch<SetStateAction<ProductCartData | undefined>>
  setShipping: Dispatch<SetStateAction<FreightResponse[] | undefined>>
  setShowError: Dispatch<SetStateAction<boolean>>
  uniqueVoltages: AttributeProps[]
  variationActive: Variation
  voltageProduct: { slug?: string; valor?: string }
}

const MAX_QUANTITY = 10

export const ContentValuesColumn: React.FC<ContentValuesColumnProps> = ({
  adActive,
  ads,
  dataProductCart,
  freight,
  hasWarranty,
  route,
  setDataProductCart,
  setShipping,
  setShowError,
  uniqueVoltages,
  variationActive,
  voltageProduct,
}) => {
  const { reward } = useContext(ContextoGlobal)
  const freightRef = useRef<any>(null)

  const [productQuantity, setProductQuantity] = useState<string | number>(1)
  const [shippingLoading, setShippingLoading] = useState(false)

  const discountPercentage = variationActive?.anuncios?.find(
    (ad) => ad.id === adActive.id
  )?.preco.percentual_desconto
  const existsSellers = variationActive?.anuncios?.length > 0
  const hasStock = adActive?.estoque || variationActive?.anuncios?.[0]?.estoque
  const price = adActive.preco

  const promotionItems = variationActive?.anuncios?.find(
    (ad) => ad.id === adActive.id
  )?.promocao_item?.promocao_cabecalho
  const promotionImages = {
    mobile: promotionItems?.mobile_midia?.url,
    desktop: promotionItems?.desktop_midia?.url,
  }

  const treatedSellerNameStyles = (sellerName: string): FlexProps => {
    if (sellerName.includes('Sul'))
      return { alignItems: 'flex-start', flexDir: 'column', gap: 'unset' }

    return { alignItems: 'center', flexDir: 'row', gap: '0.3rem' }
  }

  return (
    <Box
      backgroundColor={theme.colors.white}
      border="0.063rem solid #E3E3E8"
      borderRadius="0.375rem"
      padding="1.5rem"
      position="relative"
    >
      {!!hasStock &&
        displayDiscountTagAndEvent({
          discountPercentage,
          isAvailable: !!hasStock,
        }) && (
          <Flex position="absolute" right="0.25rem">
            <PromotionFlag promotionImages={promotionImages} />
          </Flex>
        )}

      {existsSellers && !!adActive?.seller && (
        <Flex color={theme.colors.grey[300]} flexDir="column" maxW="86%">
          <Text fontSize="0.875rem" mb="0">
            Vendido por{' '}
            <chakra.span fontSize="0.875rem" fontWeight="600">
              Gazin
            </chakra.span>
          </Text>
          <Flex
            fontSize="0.875rem"
            mb={{ base: '1rem', md: '1.5rem' }}
            {...treatedSellerNameStyles(adActive.seller.nome_fantasia)}
          >
            <Text fontSize="0.875rem" mb="0">
              Entregue por
            </Text>
            <Text fontSize="0.875rem" fontWeight="600" noOfLines={1} mb="0">
              {adActive.seller.nome_fantasia}
            </Text>
          </Flex>
        </Flex>
      )}

      {hasStock ? (
        <>
          <ProductValues
            adActive={adActive}
            discountPercentage={discountPercentage}
            promotionImages={promotionImages}
          />

          <Flex flexDir="column" my={{ base: '1rem', md: '1.5rem' }}>
            {hasStock && (
              <>
                {reward.points !== 0 && reward.value !== 0 && (
                  <ProductRewardsPoints
                    campaign={reward}
                    productValue={price.por}
                  />
                )}
                <Box w="full">
                  <ProductQuantity
                    suffix={
                      adActive.estoque < MAX_QUANTITY
                        ? '(Últimas unidades)'
                        : '(10 unidades por compra)'
                    }
                    label={`Quantidade: ${productQuantity}`}
                    onChange={(value) => setProductQuantity(value)}
                    value={productQuantity}
                    onlyShowSuffixWhenListIsOpen={
                      adActive.estoque > MAX_QUANTITY
                    }
                    options={makeArrayQuantity(
                      adActive.estoque > MAX_QUANTITY
                        ? MAX_QUANTITY
                        : adActive.estoque
                    )}
                  />
                </Box>
              </>
            )}

            <BuyNowAndAddCartButtons
              activeVariationAdId={variationActive.anuncios[0].id}
              adActiveId={adActive.id}
              ads={ads}
              dataProductCart={dataProductCart}
              existsSellers={existsSellers}
              freight={freight}
              freightRef={freightRef}
              hasWarranty={hasWarranty}
              shippingLoading={shippingLoading}
              productQuantity={productQuantity}
              route={route}
              setDataProductCart={setDataProductCart}
              setShowError={setShowError}
              uniqueVoltages={uniqueVoltages}
              voltageProduct={voltageProduct}
            />
          </Flex>

          <Flex borderTop="0.063rem solid #E3E3E8">
            {existsSellers ? (
              <SearchShipping
                adId={adActive.id}
                amount={Number(productQuantity)}
                label="Calcular frete e prazo"
                loading={shippingLoading}
                ref={freightRef}
                setLoading={setShippingLoading}
                setShipping={setShipping}
                variationId={variationActive.id}
              />
            ) : (
              <SearchShipping
                adId={variationActive.anuncios[0].id}
                label="Calcular frete e prazo"
                loading={shippingLoading}
                setLoading={setShippingLoading}
                setShipping={setShipping}
                variationId={variationActive.id}
              />
            )}
          </Flex>
        </>
      ) : (
        <Box borderTop="0.063rem solid #E3E3E8" pt="1rem">
          <Text
            color={theme.colors.grey[100]}
            fontSize="1.125rem"
            fontWeight="700"
            mb="0.5rem"
          >
            Produto indisponível
          </Text>
          <Text color={theme.colors.grey[300]} fontSize="0.875rem" mb="0">
            No momento, este produto encontra-se indisponível, pois já vendemos
            todo o estoque.
          </Text>
        </Box>
      )}
    </Box>
  )
}
